import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { GoogleLogo } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy first.");
      return;
    }
    setBusy(true);
    try {
      await register(email, password, name, accepted);
      toast.success("Account created");
      nav("/pricing");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    if (!accepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy first.");
      return;
    }
    // Mark acceptance in localStorage so AuthCallback can forward it to the backend
    localStorage.setItem("pending_tos_accept", "1");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/pricing";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-foreground text-background p-12">
        <Link to="/" className="flex items-center gap-2">
          <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
          <span className="font-display text-xl font-bold">Easy Mail</span>
        </Link>
        <div>
          <p className="font-display text-5xl leading-none mb-6">Start now. Cancel any time.</p>
          <p className="text-base text-background/70 max-w-md">Reclaim your inbox.</p>
        </div>
        <div className="font-mono text-xs uppercase tracking-widest text-background/50">v0.1 — Public Beta</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="font-display text-4xl mb-2">Create account</h1>
          <p className="text-muted-foreground mb-8">Choose a plan and start cleaning your inbox today.</p>

          <label className="flex items-start gap-3 mb-5 text-sm cursor-pointer" data-testid="tos-row">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="rounded-none mt-0.5" data-testid="tos-checkbox" />
            <span>
              I agree to the <Link to="/legal/terms_and_conditions" className="underline">Terms &amp; Conditions</Link> and <Link to="/legal/privacy_policy" className="underline">Privacy Policy</Link>.
            </span>
          </label>

          <Button
            onClick={googleSignIn}
            variant="outline"
            disabled={!accepted}
            className="w-full rounded-none gap-2 mb-4 h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="google-signup-btn"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-muted-foreground my-4">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="rounded-none" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="pw">Password</Label>
              <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-none" data-testid="register-password" />
            </div>
            <Button
              type="submit"
              disabled={busy || !accepted}
              className="w-full rounded-none h-11 btn-brutalist disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6">
            Already a user? <Link to="/login" className="underline">Sign in</Link>
          </p>
        </div>
      </div>
      <div data-testid="footer-legal" className="border-t border-border py-6 text-center text-xs text-muted-foreground"><Link to="/legal" className="underline mx-2">Legal Documents and Policies</Link></div>
    </div>
  );
}
